import { supabaseClient } from './supabase';

export interface StatewideGHGData {
  year: number;
  total_emissions: number;
}

export interface CityGHGData {
  geofips: number;
  location_name: string;
  years: number[];
  emissions: number[];
}

/**
 * Fetch statewide total GHG emissions (sum of all locations) by year
 */
export async function fetchStatewideGHGData(): Promise<StatewideGHGData[]> {
  try {
    const { data, error } = await supabaseClient
      .from('ghg_inventory_precalculated')
      .select('year, total_emissions')
      .gte('year', 2005)
      .lte('year', 2023)
      .order('year', { ascending: true });

    if (error) {
      throw new Error(`Error fetching statewide GHG data: ${error.message}`);
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Aggregate by year - sum total_emissions for all locations per year
    const aggregatedByYear = new Map<number, number>();
    
    data.forEach(row => {
      if (row.total_emissions !== null && row.total_emissions !== undefined) {
        const currentTotal = aggregatedByYear.get(row.year) || 0;
        aggregatedByYear.set(row.year, currentTotal + row.total_emissions);
      }
    });

    // Convert to array and sort by year
    const result: StatewideGHGData[] = Array.from(aggregatedByYear.entries())
      .map(([year, total_emissions]) => ({ year, total_emissions }))
      .sort((a, b) => a.year - b.year);

    return result;
  } catch (error) {
    console.error('Error in fetchStatewideGHGData:', error);
    throw error;
  }
}

/**
 * Fetch Alameda County cities' GHG emissions data (one dataset per city)
 */
export async function fetchAllCitiesGHGData(): Promise<CityGHGData[]> {
  try {
    // Get ALL cities with data
    const { data: locations, error: locationsError } = await supabaseClient
      .from('ghg_inventory_precalculated')
      .select('geofips, location_name, geography_type, county_name')
      .eq('geography_type', 'City')
      .order('location_name', { ascending: true });

    if (locationsError) {
      throw new Error(`Error fetching locations: ${locationsError.message}`);
    }

    if (!locations || locations.length === 0) {
      return [];
    }

    // Get unique cities
    const uniqueCities = Array.from(
      new Map(locations.map(loc => [loc.geofips, loc])).values()
    );

    // Fetch data for each city
    const citiesData: CityGHGData[] = [];

    for (const city of uniqueCities) {
      const { data: cityData, error: cityError } = await supabaseClient
        .from('ghg_inventory_precalculated')
        .select('year, total_emissions')
        .eq('geofips', city.geofips)
        .gte('year', 2005)
        .lte('year', 2023)
        .order('year', { ascending: true });

      if (cityError) {
        console.warn(`Error fetching data for ${city.location_name}:`, cityError);
        continue;
      }

      if (!cityData || cityData.length === 0) {
        continue;
      }

      // Build years and emissions arrays
      const years: number[] = [];
      const emissions: number[] = [];

      cityData.forEach(row => {
        if (row.total_emissions !== null && row.total_emissions !== undefined) {
          years.push(row.year);
          emissions.push(row.total_emissions);
        }
      });

      // Only add cities with at least one data point
      if (years.length > 0) {
        citiesData.push({
          geofips: city.geofips,
          location_name: city.location_name,
          years,
          emissions
        });
      }
    }

    return citiesData.sort((a, b) => a.location_name.localeCompare(b.location_name));
  } catch (error) {
    console.error('Error in fetchAllCitiesGHGData:', error);
    throw error;
  }
}

/**
 * Fetch Bay Area counties' GHG emissions data (one dataset per county)
 * Bay Area counties: Alameda, Contra Costa, Marin, Napa, San Francisco, San Mateo, Santa Clara, Solano, Sonoma
 */
export async function fetchAllCountiesGHGData(): Promise<CityGHGData[]> {
  try {
    // Fetch ALL counties that have inventory rows
    const { data: locations, error: locationsError } = await supabaseClient
      .from('ghg_inventory_precalculated')
      .select('geofips, location_name, geography_type')
      .eq('geography_type', 'County')
      .order('location_name', { ascending: true });

    if (locationsError) {
      throw new Error(`Error fetching locations: ${locationsError.message}`);
    }

    if (!locations || locations.length === 0) {
      return [];
    }

    // Get unique counties
    const uniqueCounties = Array.from(
      new Map(locations.map(loc => [loc.geofips, loc])).values()
    );

    // Fetch data for each county
    const countiesData: CityGHGData[] = [];

    for (const county of uniqueCounties) {
      const { data: countyData, error: countyError } = await supabaseClient
        .from('ghg_inventory_precalculated')
        .select('year, total_emissions')
        .eq('geofips', county.geofips)
        .gte('year', 2005)
        .lte('year', 2023)
        .order('year', { ascending: true });

      if (countyError) {
        console.warn(`Error fetching data for ${county.location_name}:`, countyError);
        continue;
      }

      if (!countyData || countyData.length === 0) {
        continue;
      }

      // Build years and emissions arrays
      const years: number[] = [];
      const emissions: number[] = [];

      countyData.forEach(row => {
        if (row.total_emissions !== null && row.total_emissions !== undefined) {
          years.push(row.year);
          emissions.push(row.total_emissions);
        }
      });

      // Only add counties with at least one data point
      if (years.length > 0) {
        countiesData.push({
          geofips: county.geofips,
          location_name: county.location_name,
          years,
          emissions
        });
      }
    }

    return countiesData.sort((a, b) => a.location_name.localeCompare(b.location_name));
  } catch (error) {
    console.error('Error in fetchAllCountiesGHGData:', error);
    throw error;
  }
}

